import { Component, computed, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';

import { AffiliateLinkService } from '../../core/affiliate/affiliate-link.service';
import { StoreLogo } from '../store-logo/store-logo';

/**
 * The canonical SavingArena product card (the design finalised on the /deals page):
 * product image with a circular store-logo badge pinned to its top-left, then title,
 * star rating + review count, `was <old> SAVE <amount>`, and the current price.
 *
 * Every optional row hides itself when its data is missing, so the same card works for
 * feeds that only carry an image + title + price.
 *
 * Usage: <app-product-card [title]="..." [image]="..." [price]="..." ... />
 */
@Component({
  selector: 'app-product-card',
  imports: [RouterLink, StoreLogo],
  templateUrl: './product-card.html',
  styleUrl: './product-card.scss',
})
export class ProductCard {
  // Outbound links are built by one service so the URL, the rel and the GA4 event cannot drift.
  private readonly affiliate = inject(AffiliateLinkService);

  readonly title = input('');
  readonly image = input('');
  readonly storeName = input('');
  readonly storeLogoUrl = input('');
  /** Second image swapped in on hover — pass a genuinely different angle, not the same shot. */
  readonly hoverImage = input('');
  readonly price = input('');
  readonly oldPrice = input('');
  /** Pre-formatted savings label, e.g. "SAVE $30.00". Falls back to a computed one when omitted. */
  readonly save = input('');
  readonly reviews = input<number | null>(null);
  /** 0-5; drives how many filled stars render. Defaults to 5 (matching the reference design). */
  readonly rating = input(5);
  /** Internal route ('/collection') or absolute affiliate URL ('https://…'). */
  readonly href = input('');
  /**
   * Internal route to this product's own page. When set, a "View" button appears over the image on
   * hover. Kept separate from `href`, which usually points off-site to the merchant.
   */
  readonly detailHref = input('');
  /**
   * Explicit merchant/affiliate URL for "Buy Now". Only needed when the card's main `href` is an
   * in-app path — pages whose `href` is already the merchant URL are handled by `buyLink` below.
   */
  readonly buyHref = input('');
  /**
   * Product id. When set, "Buy Now" goes through the `/go/p/:id` redirect instead of the
   * raw affiliate URL, so the merchant link never appears in the markup or the browser status bar
   * (and the click is recorded server-side). Falls back to the raw URL when no id is supplied.
   */
  readonly productId = input('');
  /**
   * Whether this product lives off-site.
   *
   * Pages used to signal this by passing the merchant URL as `href` and letting the card sniff it
   * with a regex. That worked, but it meant the raw affiliate URL had to be in the payload — and
   * therefore in the page source — purely to communicate a boolean. Passing the boolean lets the
   * URL stay server-side, where `/go/p/:id` resolves it.
   *
   * Defaults false and is OR-ed with the old regex, so a host still passing a URL is unaffected.
   */
  readonly external = input(false);

  // Product feeds mix hosts and half of them rot, so a dead URL must fall back to the placeholder
  // rather than leaving a broken-image box.
  protected readonly imageFailed = signal(false);
  protected readonly hoverImageFailed = signal(false);
  // The alternate angle is the merchant's own full-size photo (~190 KB each). Rendering all of them
  // up front cost ~4 MB on a 24-card grid for an effect most visitors never trigger, so the element
  // is only created once the pointer (or keyboard focus) actually reaches this card. Starts false on
  // both server and client, so hydration still matches.
  protected readonly hoverArmed = signal(false);

  protected readonly showImage = computed(
    () => this.image().trim().length > 0 && !this.imageFailed(),
  );
  // Only meaningful alongside a visible main image: the hover shot cross-fades over it.
  protected readonly showHoverImage = computed(
    () =>
      this.hoverArmed() &&
      this.showImage() &&
      this.hoverImage().trim().length > 0 &&
      !this.hoverImageFailed(),
  );

  protected readonly isExternal = computed(
    () => this.external() || /^https?:\/\//i.test(this.href().trim()),
  );

  /**
   * The two card actions are derived rather than requiring every page to pass both links, because
   * `href` already means different things across the site: on the home page's deals it is the
   * merchant's affiliate URL, while on the store page it is the in-app `/<productSlug>` path.
   *
   * So: an off-site link is a buy link, an in-app one is a detail link, and an explicit `buyHref` /
   * `detailHref` always wins. That keeps "Buy Now" from ever pointing at an internal page — which
   * is exactly what a blanket `href` binding would have produced on half the pages.
   */
  /**
   * The redirect for this product, or '' when there is no id to build one from.
   *
   * Every outbound anchor on the card — image, title and "Buy Now" — goes through this, so the raw
   * affiliate URL never reaches an `href`. Putting it on only the button still leaked it via the
   * image and title links and the browser's status bar.
   */
  private readonly redirectLink = computed(() => this.affiliate.productUrl(this.productId()));

  /** The off-site destination for the image/title links — the redirect when possible, else the raw URL. */
  protected readonly externalLink = computed(() => {
    if (!this.isExternal()) return '';

    return this.redirectLink() || this.href().trim();
  });

  protected readonly buyLink = computed(() => {
    // When the card can redirect and the product is off-site, the redirect IS the buy link. This
    // used to be reached only through `raw`, which came from the very URL being removed.
    const redirect = this.redirectLink();
    if (redirect && this.isExternal()) return redirect;

    const raw = this.buyHref().trim() || (this.isExternal() ? this.href().trim() : '');
    if (!raw) return '';

    return redirect || raw;
  });

  protected readonly detailLink = computed(() => {
    const explicit = this.detailHref().trim();
    if (explicit) return explicit;

    const href = this.href().trim();
    return href && !this.isExternal() ? href : '';
  });

  /** True when the buy link goes off-site, so it opens in a new tab with `nofollow sponsored`. */
  protected readonly isBuyExternal = computed(() => /^https?:\/\//i.test(this.buyLink()));
  protected readonly stars = computed(() =>
    '★★★★★'.slice(0, Math.max(0, Math.min(5, this.rating()))),
  );
  protected readonly hasPriceCompare = computed(() => this.oldPrice().trim().length > 0);

  // "SAVE $30.00" is supplied by most feeds; derive it when only both prices are known.
  protected readonly saveLabel = computed(() => {
    const explicit = this.save().trim();
    if (explicit) return explicit;

    const oldValue = this.toNumber(this.oldPrice());
    const newValue = this.toNumber(this.price());
    if (oldValue === null || newValue === null || oldValue <= newValue) return '';

    const prefix = this.currencyOf(this.price()) || this.currencyOf(this.oldPrice()) || '$';
    // "EUR" needs a space before the amount, "$" and "£" do not.
    const separator = /[A-Za-z]$/.test(prefix) ? ' ' : '';

    return `SAVE ${prefix}${separator}${(oldValue - newValue).toFixed(2)}`;
  });

  private toNumber(value: string): number | null {
    const parsed = Number.parseFloat(value.replace(/[^0-9.]/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }

  // Prices arrive pre-formatted by whichever feed supplied them ("$30.00", "EUR 14.00", "£9.49"), so
  // the savings label reuses that currency instead of stamping a dollar sign onto a euro price.
  private currencyOf(value: string): string {
    const match = value.trim().match(/^[^\d]+/);
    return match ? match[0].trim() : '';
  }
}
